/**
 * Bounded Context: Conversation — Pinia store
 *
 * Mantém a lista de mensagens em memória. Cap razoável para evitar
 * render O(n²) em v0.1 (Pyodide em HLOS-7 trará paginação real).
 *
 * HLOS-219: streaming incremental de tokens (CC-04 — progresso real,
 * não fake). appendStream() acumula tokens em `partialText` da última
 * mensagem companion; finalizeStream() promove partialText para text
 * e marca streaming=false.
 *
 * HLOS-319: append() aceita `attachments` (imagem/sketch) para mensagens
 * do learner. Anexos são renderizados inline no MessageBubble.
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import type { Message, MessageAttachment, Role } from "./types";

const MAX_MESSAGES = 200;

export const useConversationStore = defineStore("conversation", () => {
  const messages = ref<Message[]>([]);

  /** Append determinístico — gera id estável sem random seed. */
  let counter = 0;
  function nextId(): string {
    counter += 1;
    return `msg_${counter}`;
  }

  function append(
    role: Role,
    text: string,
    offer?: string | null,
    attachments?: readonly MessageAttachment[],
  ): Message {
    const hasAttachments = attachments && attachments.length > 0;
    const msg: Message = {
      id: nextId(),
      role,
      text,
      createdAt: Date.now(),
      // HLOS-230: propaga oferta (CC-01 affordance visível). Omite campo
      // quando offer é null/undefined/empty para serialização limpa.
      ...(offer ? { offer } : {}),
      // HLOS-319: anexos (imagem/sketch) inline.
      ...(hasAttachments ? { attachments } : {}),
    };
    const next = [...messages.value, msg];
    messages.value = next.length > MAX_MESSAGES ? next.slice(next.length - MAX_MESSAGES) : next;
    return msg;
  }

  /**
   * HLOS-219: cria mensagem companion vazia em modo streaming.
   * Retorna o id da mensagem para o caller acumular tokens via appendStream.
   */
  function appendStreaming(role: Role): string {
    const id = nextId();
    const msg: Message = {
      id,
      role,
      text: "",
      partialText: "",
      streaming: true,
      createdAt: Date.now(),
    };
    messages.value = [...messages.value, msg];
    return id;
  }

  /**
   * HLOS-219: acumula token à mensagem streaming.
   * Garante reatividade: cada token emite novo array (Vue 3 ref reativo).
   */
  function appendStream(id: string, token: string): void {
    messages.value = messages.value.map((m) => {
      if (m.id !== id) return m;
      const partialText = (m.partialText ?? "") + token;
      return { ...m, partialText };
    });
  }

  /**
   * HLOS-219: finaliza streaming — promove partialText para text e
   * marca streaming=false. Reativo para fechar <StreamingMessage>.
   */
  function finalizeStream(id: string): void {
    messages.value = messages.value.map((m) => {
      if (m.id !== id) return m;
      return {
        ...m,
        text: m.partialText ?? m.text,
        streaming: false,
      };
    });
  }

  function clear(): void {
    messages.value = [];
  }

  return {
    messages,
    maxMessages: MAX_MESSAGES,
    append,
    appendStreaming,
    appendStream,
    finalizeStream,
    clear,
  };
});
